import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { downloadMonologueScript } from "@/lib/pdf";
import ShareButton from "@/components/ShareButton";

const ease = [0.16, 1, 0.3, 1];

// Approximate per-line cues for the on-screen Hindi caption while the
// audio plays. Original Hindi only — English CC removed (per Ranjana's note,
// the previous machine translation was inaccurate). The full transcript panel
// below also displays in Hindi only.
const cues = [
  { t: 0.0,  hi: "Mai roz sochti hu Vandana…" },
  { t: 3.3,  hi: "Aakhir aisi kaunsi sidi hai jisse mai tum tak pahuch saku…" },
  { t: 8.6,  hi: "Tumhe jalte aur tadapte dekh saku…" },
  { t: 12.6, hi: "Mere iss battar Zindagi ka ek hi maksad hai…" },
  { t: 16.2, hi: "Tumhe aise ghau du jo roz jale…" },
  { t: 20.9, hi: "Jaise mere aur baakiyo ke…jinko tum bhool chuki…" },
  { t: 24.0, hi: "Zakhm itne taaza aur gehre ho pui se bhare…ki jab jale toh tumhe humari yaad aaye…" },
  { t: 27.1, hi: "……" },
  { t: 29.6, hi: "Phir mujhe khayal aata hai tumhare bete ka…" },
  { t: 38.5, hi: "Usske naazuk aur komal jism ka…" },
  { t: 44.7, hi: "Tumhare darr ki…ek hi wajah ho sakti hai…" },
  { t: 55.2, hi: "Vandana…" },
];

// Pure-text transcript exactly as written by Ranjana, with the
// English translation she approved (shown in the expandable panel + PDF only,
// never in the live captions).
const fullTranscript = [
  { hi: "Mai roz sochti hu Vandana…",                                                  en: "I think about it everyday Vandana…" },
  { hi: "Aakhir aisi kaunsi sidi hai jisse mai tum tak pahuch saku…",                  en: "What ladder I must climb to reach you…" },
  { hi: "Tumhe jalte aur tadapte dekh saku…",                                          en: "The one that will let me watch you fall apart…" },
  { hi: "Mere iss battar Zindagi ka ek hi maksad hai…",                                en: "My despicable life has a singular purpose…" },
  { hi: "Tumhe aise ghau du jo roz jale…",                                             en: "To give you scars that will burn for life…" },
  { hi: "Jaise mere aur baakiyo ke…jinko tum bhool chuki…",                            en: "The ones identical to mine and others…whom you've clearly forgotten…" },
  { hi: "Zakhm itne taaza aur gehre ho pui se bhare…ki jab jale toh tumhe humari yaad aaye…", en: "Wounds so fresh and deep filled with pus…that they remind you of us…" },
  { hi: "……",                                                                          en: "……" },
  { hi: "Phir mujhe khayal aata hai tumhare bete ka…",                                 en: "Then…I suddenly think about your son…" },
  { hi: "Usske naazuk aur komal jism ka…",                                             en: "About his innocent and soft facade…" },
  { hi: "Tumhare darr ki…ek hi wajah ho sakti hai…",                                   en: "You might afterall, have a reason to fear…" },
  { hi: "Vandana…",                                                                    en: "Vandana…" },
];

export default function Monologue() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errored, setErrored] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a || errored) return;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => setErrored(true));
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setCurrentTime(a.currentTime);
    setProgress((a.currentTime / a.duration) * 100);
  };

  const activeCueIndex = (() => {
    let idx = -1;
    for (let i = 0; i < cues.length; i++) {
      if (currentTime >= cues[i].t) idx = i;
      else break;
    }
    return idx;
  })();
  const activeCue = activeCueIndex >= 0 ? cues[activeCueIndex] : null;

  return (
    <section
      id="monologue"
      data-testid="monologue-section"
      className="relative py-24 md:py-40 bg-[#0a0a0a] border-y border-white/5"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-10">
          <div className="overline">
            <span className="accent-bar" />
            Showreel
          </div>
          <ShareButton slug="monologue" label="Share monologue" testId="share-monologue" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease }}
          className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center"
        >
          <div className="md:col-span-7">
            <div
              className="relative img-frame aspect-[16/10] crosshair overflow-hidden"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, rgba(192,21,21,0.15), #0a0a0a 60%)",
              }}
            >
              <img
                src="/assets/p-purple-top.jpg"
                alt="Monologue still"
                className="absolute inset-0 w-full h-full object-cover opacity-40"
                style={{ filter: "grayscale(85%) contrast(1.1)" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />

              <video
                ref={audioRef}
                data-testid="monologue-audio"
                src="/assets/monologue.mp4"
                playsInline
                preload="none"
                onTimeUpdate={onTimeUpdate}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                onEnded={() => {
                  setPlaying(false);
                  setProgress(100);
                }}
                onError={() => {
                  setErrored(true);
                  setPlaying(false);
                }}
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: "none",
                }}
              />

              <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 z-10 px-6">
                {errored ? (
                  <a
                    data-testid="monologue-fallback-link"
                    href="https://www.ranjanabaruah.com/about"
                    target="_blank"
                    rel="noreferrer"
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full border border-white/40 backdrop-blur-md bg-white/5 flex items-center justify-center transition-all hover:bg-[#c01515] hover:border-[#c01515] hover:scale-110"
                  >
                    <svg width="22" height="26" viewBox="0 0 22 26" fill="white">
                      <path d="M0 0 L22 13 L0 26 Z" />
                    </svg>
                  </a>
                ) : (
                  <button
                    data-testid="play-monologue-btn"
                    onClick={togglePlay}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full border border-white/40 backdrop-blur-md bg-white/5 flex items-center justify-center transition-all hover:bg-[#c01515] hover:border-[#c01515] hover:scale-110"
                    aria-label={playing ? "Pause monologue" : "Play monologue"}
                  >
                    {playing ? (
                      <span className="font-mono text-3xl text-white pb-1">‖</span>
                    ) : (
                      <svg width="22" height="26" viewBox="0 0 22 26" fill="white">
                        <path d="M0 0 L22 13 L0 26 Z" />
                      </svg>
                    )}
                  </button>
                )}

                {/* Hindi-only live caption (English CC removed) */}
                <div className="h-28 flex items-end justify-center w-full max-w-2xl">
                  <AnimatePresence mode="wait">
                    {playing && activeCue ? (
                      <motion.p
                        key={activeCueIndex}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.45, ease }}
                        data-testid="active-cue"
                        className="font-display italic text-2xl md:text-3xl text-center text-white"
                      >
                        {activeCue.hi}
                      </motion.p>
                    ) : (
                      <motion.p
                        key="static"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="font-display italic text-2xl md:text-3xl text-center text-white/90 max-w-xl"
                      >
                        "Mai roz sochti hu Vandana…"
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>

                {errored && (
                  <span
                    data-testid="monologue-error-note"
                    className="overline text-white/60"
                  >
                    Open the reel in a new tab →
                  </span>
                )}
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/10">
                <div
                  className="h-full bg-[#c01515] transition-all duration-200"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="absolute top-4 left-4 overline text-[#c01515] z-10">
                ● {playing ? "PLAYING" : "REC"}
              </div>
              <div className="absolute top-4 right-4 overline z-10">
                Hindi · Spoken Word
              </div>
            </div>
          </div>

          <div className="md:col-span-4 md:col-start-9">
            <h3
              className="font-display font-light leading-[0.95] tracking-[-0.03em]"
              style={{ fontSize: "clamp(2.2rem, 5vw, 4.5rem)" }}
            >
              Hear my <span className="italic text-[#c01515]">monologue</span>.
            </h3>
            <p className="mt-6 text-[#a3a3a3] leading-relaxed">
              A short performance — built on the slow weight of pause, gaze
              and confession. The sound of a thought before it becomes a
              consequence.
            </p>
            {errored ? (
              <a
                data-testid="open-monologue-btn"
                href="https://www.ranjanabaruah.com/about"
                target="_blank"
                rel="noreferrer"
                className="btn-sharp mt-10 inline-flex"
              >
                <span>Open in legacy site</span>
                <span>→</span>
              </a>
            ) : (
              <button
                data-testid="open-monologue-btn"
                onClick={togglePlay}
                className="btn-sharp mt-10"
              >
                <span>{playing ? "Pause the reel" : "Play the reel"}</span>
                <span>{playing ? "‖" : "→"}</span>
              </button>
            )}
            <button
              data-testid="toggle-transcript-btn"
              onClick={() => setShowTranscript((s) => !s)}
              className="block mt-6 nav-link"
            >
              {showTranscript ? "Hide transcript ↑" : "Read full transcript ↓"}
            </button>
            <button
              data-testid="download-pdf-btn"
              onClick={downloadMonologueScript}
              className="block mt-3 nav-link"
            >
              Download script · PDF ↓
            </button>
          </div>
        </motion.div>

        {/* Full transcript — Hindi original + English translation side by side.
            English is intentionally shown ONLY here (and in the PDF) — never
            in the live captions. */}
        <AnimatePresence>
          {showTranscript && (
            <motion.div
              key="transcript"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.6, ease }}
              data-testid="transcript-panel"
              className="mt-16 overflow-hidden"
            >
              <div className="dotted-line" />
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pt-12">
                <div className="md:col-span-2">
                  <p className="overline">Narration</p>
                </div>
                <div className="md:col-span-5">
                  <p className="overline mb-6 text-[#a3a3a3]">
                    Original · Hindi
                  </p>
                  <div className="space-y-4 font-display text-xl md:text-2xl text-white leading-relaxed italic">
                    {fullTranscript.map((line, i) => (
                      <p key={`hi-${i}`}>{line.hi}</p>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-5">
                  <p className="overline mb-6 text-[#a3a3a3]">
                    Translation · English
                  </p>
                  <div className="space-y-4 font-display text-xl md:text-2xl text-white leading-relaxed">
                    {fullTranscript.map((line, i) => (
                      <p key={`en-${i}`}>{line.en}</p>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
